import { PlaycastGamepadXInput, PlaycastKeyDetail, XInput } from "@playcastdotio/CoreTypes";
import { generators } from "@playcastdotio/Messaging";
import { ipcSend } from "../../messaging/protocols/ipc";
import keycode from 'keyboard-key';
import { hostMacroConfigState } from "../../stores/shared";

const createKeyDetail = (keyCode: number, shift: boolean): PlaycastKeyDetail => {
  return {
    keyCode,
    location: 'standard',
    source: {
      type: 'keyboard',
      code: keyCode,
    },
    modifiers: {
      ctrl: false,
      alt: false,
      shift,
      meta: false,
    },
    displayName: 'host',
  };
};

const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

const sendKeyDown = (keyCode: number, shift: boolean) => {
  const keyDetail = createKeyDetail(keyCode, shift);
  const keyDownMessage = generators.keyboard.down('host', keyDetail);

  ipcSend(keyDownMessage);
};

const sendKeyUp = (keyCode: number, shift: boolean) => {
  const keyDetail = createKeyDetail(keyCode, shift);
  const keyUpMessage = generators.keyboard.up('host', keyDetail);

  ipcSend(keyUpMessage);
};

const sendKeyPress = async (keyCode: number, shift: boolean, hold: number) => {
  sendKeyDown(keyCode, shift);
  await delay(hold);
  sendKeyUp(keyCode, shift);
  await delay(20);
};

const toXInputButtonMask = (button: string): number => {
  switch (button) {
    // Face buttons
    case 'A': return 0x1000;
    case 'B': return 0x2000;
    case 'X': return 0x4000;
    case 'Y': return 0x8000;

    // D-pad
    case 'DUP': return 0x0001;
    case 'DDOWN': return 0x0002;
    case 'DLEFT': return 0x0004;
    case 'DRIGHT': return 0x0008;

    // Menu/View
    case 'START': return 0x0010; // Menu
    case 'BACK':  return 0x0020; // View

    // Stick clicks
    case 'LS': return 0x0040; // Left stick click
    case 'RS': return 0x0080; // Right stick click

    // Bumpers
    case 'LB': return 0x0100;
    case 'RB': return 0x0200;

    // Guide (note: often blocked by XInput in many contexts, but included for completeness)
    case 'GUIDE': return 0x0400;

    default:
      return 0;
  }
};

const neutralXInput = (): XInput => ({
  wButtons: 0,
  bLeftTrigger: 0,
  bRightTrigger: 0,
  sThumbLX: 0,
  sThumbLY: 0,
  sThumbRX: 0,
  sThumbRY: 0,
});

const applyButtonToXInput = (button: string, x: XInput): XInput => {
  const mask = toXInputButtonMask(button);
  if (mask) return { ...x, wButtons: x.wButtons | mask };

  switch (button) {
    case 'LT': return { ...x, bLeftTrigger: 255 };
    case 'RT': return { ...x, bRightTrigger: 255 };
    default: return x;
  }
};

const sendGamepadTap = async (button: string, hold: number) => {
  const downX = applyButtonToXInput(button, neutralXInput());

  const base: Omit<PlaycastGamepadXInput, 'xInput'> = {
    gamepadId: 0,
    playerCoordinates: {
      origin: 'bottomLeft',
      dimensions: { x: 0, y: 0 },
      display: { x: 0, y: 0 },
    },
  };

  ipcSend(generators.gamepad.setXInput('host', { ...base, xInput: downX }));
  await delay(hold);

  ipcSend(generators.gamepad.setXInput('host', { ...base, xInput: neutralXInput() }));
  await delay(20);
};

const shiftRequiredSet = new Set([
  ')', '!', '@', '#', '$', '%', '^', '&', '*', '(',
  '_', '+', '{', '}', ':', '"', '<', '>', '?', '|'
]);

const sendKeyString = async (text: string) => {
    for (const char of text) {
        const code = keycode.getCode(char);
        if (!code) continue; // Unknown keycode.

        const shift = (char === char.toUpperCase() && char !== char.toLowerCase()) || shiftRequiredSet.has(char);

        if (shift) {
            await sendKeyDown(16, shift); // Send shift down
            await delay(20);
        }
        await sendKeyPress(code, shift, 20);

        if (shift) {
            await sendKeyUp(16, shift); // Send shift down
            await delay(20);
        }
    }
}

const DEFAULT_TIMING_MS = 100;
const DEFAULT_HOLD_MS_KEYBOARD = 20;
// Gamepad presses default to a longer hold than keyboard presses because
// most games reject sub-frame controller inputs (a 20ms tap is ~1.2 frames
// at 60Hz and frequently gets debounced by the engine's input layer). 150ms
// gives ~9 frames of sustained pressure — past every reasonable game-side
// debounce window — and still feels like a deliberate single press, not a
// hold-action. Authors who need a shorter or longer tap can specify ^N.
// Mirror of the constant in `coordinators/game-launch/managers/MacroManager.ts`.
const DEFAULT_HOLD_MS_GAMEPAD = 150;

const parseMacroStep = (step: string): { type: 'key' | 'string' | 'gpad'; value: string, timing: number, hold: number } | null => {
  const parseMacroString = (raw: string, defaultHold: number = DEFAULT_HOLD_MS_KEYBOARD) => {
    // Find first modifier (@ or ^)
    const at = raw.indexOf('@');
    const caret = raw.indexOf('^');

    let endIdx = raw.length;
    if (at !== -1 && caret !== -1) endIdx = Math.min(at, caret);
    else if (at !== -1) endIdx = at;
    else if (caret !== -1) endIdx = caret;

    const value = raw.slice(0, endIdx);

    const timingMatch = raw.match(/@(\d+)/);
    const holdMatch   = raw.match(/\^(\d+)/);

    const timing = timingMatch ? Number(timingMatch[1]) : DEFAULT_TIMING_MS;
    const hold   = holdMatch   ? Number(holdMatch[1])   : defaultHold;

    return { value, timing, hold };
  };

  if (step.startsWith('key=')) {
    const parsed = parseMacroString(step.substring(4));
    if (!parsed) return null;
    return { type: 'key', ...parsed };
  }

  if (step.startsWith('string=')) {
    const parsed = parseMacroString(step.substring(7));
    if (!parsed) return null;
    return { type: 'string', ...parsed };
  }

  if (step.startsWith('gpad=')) {
    const parsed = parseMacroString(step.substring(5), DEFAULT_HOLD_MS_GAMEPAD);
    if (!parsed) return null;
    return { type: 'gpad', ...parsed };
  }

  // Unknown step format
  console.warn('[Macro Manager] Unknown macro step:', step);
  return null;
};

const runMacroSteps = async (steps: string[], label: string): Promise<void> => {
  if (!steps || steps.length === 0) {
    console.log(`[Macro Manager] No steps for ${label}, skipping.`);
    return;
  }

  console.log(`[Macro Manager] Running ${label} with ${steps.length} steps.`);

  for (const rawStep of steps) {
    const step = parseMacroStep(rawStep);
    if (!step) continue;

    if (step.type === 'key') {
      const keyCode = Number(step.value);
      if (Number.isNaN(keyCode)) {
        console.warn('[Macro Manager] Invalid key code in step:', rawStep);
        continue;
      }

      console.log(`[Macro Manager] Sending key press: ${keyCode}`);
      await delay(step.timing);
      await sendKeyPress(keyCode, false, step.hold);
      // Optional tiny delay between key presses so the game doesn’t miss them
    } else if (step.type === 'string') {
      console.log(`[Macro Manager] Sending string: ${step.value}`);
      await delay(step.timing);
      await sendKeyString(step.value);
    } else if (step.type === 'gpad') {
      console.log(`[Macro Manager] Sending gpad: ${step.value}`);
      await delay(step.timing);
      await sendGamepadTap(step.value, step.hold);
    }
  }

  console.log(`[Macro Manager] ${label} macro complete.`);
};

const INACTIVE_PERIOD_MS = 30_000;

let inactiveIntervalId: ReturnType<typeof setInterval> | null = null;

const startInactiveLoop = () => {
  stopInactiveLoop();

  inactiveIntervalId = setInterval(() => {
    // fire-and-forget; don't block the interval tick
    void sendInactiveMacro();
  }, INACTIVE_PERIOD_MS);
}

const stopInactiveLoop = () => {
  if (inactiveIntervalId) {
    clearInterval(inactiveIntervalId);
    inactiveIntervalId = null;
  }
}

export const sendLaunchMacro = async (): Promise<void> => {
  const launchMacro = hostMacroConfigState.currentState.onLaunchMacro;
  await runMacroSteps(launchMacro, 'Launch');

  startInactiveLoop();
};

/** Maximum time (ms) to wait for the join macro before proceeding.
 *  The macro keeps running in the background if it exceeds this cap. */
const MAX_JOIN_MACRO_WAIT_MS = 3000;

/**
 * Run the player-join macro with a time cap.
 * Resolves as soon as either the macro finishes or the cap is reached.
 * The macro continues running in the background if the cap is hit.
 */
export const sendPlayerJoinMacro = async (): Promise<void> => {
  stopInactiveLoop();

  const joinMacro = hostMacroConfigState.currentState.onJoinMacro;
  if (!joinMacro || joinMacro.length === 0) {
    console.log('[Macro Manager] No steps for Player join, skipping.');
    return;
  }

  const macroPromise = runMacroSteps(joinMacro, 'Player join');
  const timeoutPromise = new Promise<void>((resolve) => {
    setTimeout(() => {
      console.warn(`[Macro Manager] Player join macro still running after ${MAX_JOIN_MACRO_WAIT_MS}ms, proceeding without waiting.`);
      resolve();
    }, MAX_JOIN_MACRO_WAIT_MS);
  });

  await Promise.race([macroPromise, timeoutPromise]);
};

export const sendPlayerExitMacro = async (): Promise<void> => {
  const exitMacro = hostMacroConfigState.currentState.onExitMacro;
  await runMacroSteps(exitMacro, 'Player Exit');

  startInactiveLoop();
}

export const sendInactiveMacro = async (): Promise<void> => {
  const exitMacro = hostMacroConfigState.currentState.onInactiveMacro;
  await runMacroSteps(exitMacro, 'Player Exit');
}


